import { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, Modal, Empty, ErrorMessage } from '../../components';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';

const getRoles = (myRole) => {
    const base = ['participant', 'setter', 'editor'];
    if (myRole === 'superadmin') base.push('admin');
    return base;
};

export default function Users() {
    const toast = useToast();
    const { user: me } = useAuth();
    const { data: users, loading, error, reload } = useFetch(api.getAdminUsers);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [modal, setModal] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'participant' });
    const [saving, setSaving] = useState(false);

    const displayed = (users || [])
        .filter(u => !roleFilter || u.role === roleFilter)
        .filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

    const createUser = async () => {
        setSaving(true);
        try {
            await api.createAdminUser(form);
            toast('Foydalanuvchi yaratildi');
            setModal(false);
            setForm({ name: '', email: '', password: '', role: 'participant' });
            reload();
        } catch (e) { toast(e.message, 'error'); }
        finally { setSaving(false); }
    };

    const handleRoleChange = async (userId, newRole) => {
        if (!window.confirm(`Change role to "${newRole}"?`)) return;
        try {
            await api.changeUserRole(userId, newRole);
            toast('Rol yangilandi');
            reload();
        } catch (e) { toast(e.message, 'error'); }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Bu foydalanuvchini o‘chirasizmi?')) return;
        try {
            await api.deleteAdminUser(userId);
            toast('Foydalanuvchi o‘chirildi');
            reload();
        } catch (e) { toast(e.message, 'error'); }
    };

    const canChangeRole = (target) => {
        if (target.role === 'superadmin') return false;
        if (target._id === me.id) return false;
        if (target.role === 'admin' && me.role !== 'superadmin') return false;
        return true;
    };

    return (
        <div>
            <div className="page-header">
                <h1 style={{ fontSize: 24 }}>Foydalanuvchilar</h1>
                <button className="btn btn-primary" onClick={() => setModal(true)}>+ Foydalanuvchi</button>
            </div>

            <div className="filter-bar">
                <input placeholder="Ism yoki email qidirish…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }} />
                <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                    <option value="">Barcha rollar</option>
                    <option value="superadmin">Superadmin</option>
                    <option value="admin">Administrator</option>
                    <option value="setter">O‘qituvchi</option>
                    <option value="editor">Savol muharriri</option>
                    <option value="participant">Talaba</option>
                </select>
                {(search || roleFilter) && <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setRoleFilter(''); }}>Clear</button>}
            </div>

            <ErrorMessage message={error} />

            <div className="card" style={{ padding: 0 }}>
                {loading ? <Spinner /> : !displayed.length ? <Empty title="No users found" /> : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr><th>Name</th><th>Email</th><th>Role</th><th>Actions</th></tr>
                            </thead>
                            <tbody>
                                {displayed.map(u => (
                                    <tr key={u._id}>
                                        <td style={{ fontWeight: 500 }}>{u.name}{u._id === me.id && <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6 }}>(siz)</span>}</td>
                                        <td style={{ color: 'var(--muted)', fontSize: 13 }}>{u.email}</td>
                                        <td>
                                            {canChangeRole(u) ? (
                                                <select value={u.role} onChange={e => handleRoleChange(u._id, e.target.value)}
                                                    style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                                                    {getRoles(me.role).map(r => <option key={r} value={r}>{r}</option>)}
                                                </select>
                                            ) : (
                                                <span className={`badge badge-${u.role}`}>{u.role}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="td-actions">
                                                {me.role === 'superadmin' && u.role !== 'superadmin' && u._id !== me.id && (
                                                    <button className="btn btn-ghost btn-sm" onClick={async () => { try { await api.toggleUserStatus(u._id); toast(u.isActive === false ? 'Hisob faollashtirildi' : 'Hisob bloklandi'); reload(); } catch(e) { toast(e.message, 'error'); } }}>
                                                        {u.isActive === false ? 'Faollashtirish' : 'Bloklash'}
                                                    </button>
                                                )}
                                                {me.role === 'superadmin' && u.role !== 'superadmin' && u._id !== me.id && (
                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u._id)}>O‘chirish</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modal && (
                <Modal title="Create User" onClose={() => setModal(false)}
                    footer={<>
                        <button className="btn btn-ghost" onClick={() => setModal(false)}>Bekor qilish</button>
                        <button className="btn btn-primary" onClick={createUser} disabled={saving}>
                            {saving ? <span className="spinner" /> : 'Yaratish'}
                        </button>
                    </>}>
                    {['name', 'email', 'password'].map(field => (
                        <div className="form-group" key={field}>
                            <label>{field}</label>
                            <input
                                type={field === 'password' ? 'password' : 'text'}
                                value={form[field]}
                                onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                            />
                        </div>
                    ))}
                    <div className="form-group">
                        <label>Rol</label>
                        <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                            <option value="participant">Talaba</option>
                            <option value="setter">O‘qituvchi</option>
                            <option value="editor">Savol muharriri</option>
                            {me.role === 'superadmin' && <option value="admin">Administrator</option>}
                        </select>
                    </div>
                </Modal>
            )}
        </div>
    );
}